import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Video, Users, CheckCircle, XCircle, Play, Clock, X, Repeat } from 'lucide-react';
import Calendar from 'react-calendar';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, startOfWeek, endOfWeek, addMonths, subMonths, addWeeks, subWeeks, parseISO } from 'date-fns';
import { schedulesAPI, bookingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useThumbnails } from '../hooks/useThumbnails';
import toast from 'react-hot-toast';
import 'react-calendar/dist/Calendar.css';

const CATEGORIES = [
  { value: 'yoga', label: 'Yoga', icon: '🧘' },
  { value: 'dance', label: 'Dance', icon: '💃' },
  { value: 'martial_arts', label: 'Martial Arts', icon: '🥋' },
  { value: 'fitness', label: 'Fitness', icon: '💪' },
  { value: 'pilates', label: 'Pilates', icon: '🤸' },
  { value: 'meditation', label: 'Meditation', icon: '🧘' },
  { value: 'other', label: 'Other', icon: '⭐' }
];

// Helper function to format time to 12-hour format
function formatTime12Hour(timeString) {
  if (!timeString) return 'TBD';
  const [hours, minutes] = timeString.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return timeString;
  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const ampm = hours < 12 ? 'AM' : 'PM';
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

// Generate time slots from startHour to endHour (hourly only)
function generateTimeSlots(startHour = 0, endHour = 23) {
  const slots = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    const timeString = `${hour.toString().padStart(2, '0')}:00`;
    const displayTime = formatTime12Hour(timeString);
    slots.push({ timeString, displayTime, hour, minute: 0 });
  }
  return slots;
}

// Calculate top position for a schedule based on start time
function calculateSchedulePosition(startTime, slotHeight, startHour = 0) {
  if (!startTime) return 0;
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = (hours - startHour) * 60 + minutes;
  const position = (totalMinutes / 60) * slotHeight; // Hourly intervals
  // Clamp to visible range (0 to grid height)
  return Math.max(0, position);
}

// Calculate height for a schedule based on duration
function calculateScheduleHeight(durationSeconds, slotHeight) {
  if (!durationSeconds) return slotHeight; // Default to one hour
  const durationMinutes = durationSeconds / 60;
  const height = (durationMinutes / 60) * slotHeight; // Hourly intervals
  return Math.max(height, 30); // Minimum 30px for text visibility
}

// Detect and handle overlapping schedules
function layoutSchedules(schedules, slotHeight, startHour, totalHeight) {
  if (!schedules || schedules.length === 0) return [];
  
  // Calculate positions for all schedules
  const positioned = schedules.map(schedule => {
    const top = calculateSchedulePosition(schedule.startTime, slotHeight, startHour);
    const height = calculateScheduleHeight(schedule.video?.duration, slotHeight);
    const endTime = schedule.startTime ? (() => {
      const [hours, minutes] = schedule.startTime.split(':').map(Number);
      const durationMinutes = schedule.video?.duration ? schedule.video.duration / 60 : 60;
      const endMinutes = hours * 60 + minutes + durationMinutes;
      return endMinutes;
    })() : 0;
    
    return {
      ...schedule,
      top,
      height,
      endTime,
      left: 0,
      width: 1
    };
  }).filter(s => s.top < totalHeight && s.top + s.height > 0);

  // Check for schedules immediately below and ensure minimum height for 2 lines of text
  const lineHeight = 13; // 10px font * 1.3 line-height
  const minHeightForTwoLines = lineHeight * 2 + 8; // 2 lines + padding
  
  positioned.forEach((schedule, index) => {
    // Find if there's a schedule immediately below this one
    const scheduleBelow = positioned.find(s => 
      s.top > schedule.top && 
      s.top < schedule.top + schedule.height + 5 // Within 5px below
    );
    
    // If no schedule below and height is less than needed for 2 lines, increase it
    if (!scheduleBelow && schedule.height < minHeightForTwoLines) {
      schedule.height = Math.min(minHeightForTwoLines, totalHeight - schedule.top);
    }
  });

  // Sort by start time
  positioned.sort((a, b) => {
    if (a.top !== b.top) return a.top - b.top;
    return (a.endTime || 0) - (b.endTime || 0);
  });

  // Group overlapping schedules
  const columns = [];
  positioned.forEach(schedule => {
    let placed = false;
    for (let col of columns) {
      const lastInCol = col[col.length - 1];
      if (!lastInCol || lastInCol.top + lastInCol.height <= schedule.top) {
        col.push(schedule);
        placed = true;
        break;
      }
    }
    if (!placed) {
      columns.push([schedule]);
    }
  });

  // Calculate left position and width for each schedule
  const numColumns = columns.length;
  positioned.forEach(schedule => {
    const colIndex = columns.findIndex(col => col.includes(schedule));
    schedule.left = colIndex / numColumns;
    schedule.width = 1 / numColumns;
  });

  return positioned;
}

// Calculate which time slots have events and return dynamic heights based on actual event needs
function calculateDynamicSlotHeights(weekDays, getSchedulesForDate, timeSlots, startHour, baseSlotHeight) {
  const emptySlotHeight = 20; // Compressed height for empty slots
  const minEventHeight = 44; // Minimum height per event (for 2 lines of text)
  const fixedCardHeight = 35; // Fixed height for all cards
  const cardGap = 4; // Gap between stacked cards

  // Track events per slot per day to calculate needed height
  const eventsPerSlotPerDay = new Map();

  weekDays.forEach(day => {
    const daySchedules = getSchedulesForDate(day);
    daySchedules.forEach(schedule => {
      if (schedule.startTime) {
        const [hours] = schedule.startTime.split(':').map(Number);
        const slotIndex = hours - startHour;
        if (slotIndex >= 0 && slotIndex < timeSlots.length) {
          if (!eventsPerSlotPerDay.has(slotIndex)) {
            eventsPerSlotPerDay.set(slotIndex, []);
          }
          eventsPerSlotPerDay.get(slotIndex).push({ schedule, day });
        }
      }
    });
  });

  // Calculate heights based on stacking logic (same as rendering logic)
  const slotHeights = timeSlots.map((_, slotIndex) => {
    const slotEvents = eventsPerSlotPerDay.get(slotIndex) || [];
    if (slotEvents.length === 0) {
      return emptySlotHeight;
    }

    // Group events by day to calculate stacking per day
    const eventsByDay = new Map();
    slotEvents.forEach(({ schedule, day }) => {
      const dayKey = day.toISOString();
      if (!eventsByDay.has(dayKey)) {
        eventsByDay.set(dayKey, []);
      }
      eventsByDay.get(dayKey).push(schedule);
    });

    // Find the maximum stacked height across all days for this slot
    let maxSlotBottom = 0;

    eventsByDay.forEach((daySchedules) => {
      // Sort schedules by start time
      const sortedSchedules = daySchedules.sort((a, b) => {
        const timeA = a.startTime || '00:00:00';
        const timeB = b.startTime || '00:00:00';
        return timeA.localeCompare(timeB);
      });

      // Calculate stacked positions for this day (same logic as rendering)
      const stackedPositions = [];
      sortedSchedules.forEach((schedule) => {
        const [hours, minutes] = schedule.startTime.split(':').map(Number);
        const minutesInHour = minutes;

        // Position within the hour (proportional to time)
        const positionInSlot = (minutesInHour / 60) * baseSlotHeight;

        // Find overlapping events already positioned and stack below them
        // Check for VISUAL overlap, not time overlap
        let cardTop = positionInSlot;
        stackedPositions.forEach((prev) => {
          // Check if this card would visually overlap with the previous card
          if (cardTop < prev.bottom) {
            // They would overlap visually - position this card below the previous one with a gap
            cardTop = Math.max(cardTop, prev.bottom + cardGap);
          }
        });

        const cardBottom = cardTop + fixedCardHeight;
        stackedPositions.push({
          top: cardTop,
          bottom: cardBottom
        });

        // Track the maximum bottom position for this slot
        maxSlotBottom = Math.max(maxSlotBottom, cardBottom);
      });
    });

    // Slot height must accommodate the bottom-most stacked card
    return Math.max(maxSlotBottom, minEventHeight + 10);
  });
  
  const cumulativePositions = [0];
  slotHeights.forEach((height, index) => {
    cumulativePositions.push(cumulativePositions[index] + height);
  });
  
  const totalHeight = cumulativePositions[cumulativePositions.length - 1];
  
  return { slotHeights, cumulativePositions, totalHeight, eventsPerSlotPerDay };
}

// Calculate position using dynamic slot heights
function calculatePositionWithDynamicSlots(startTime, slotHeights, cumulativePositions, startHour) {
  if (!startTime) return 0;
  const [hours, minutes] = startTime.split(':').map(Number);
  const slotIndex = hours - startHour;
  
  if (slotIndex < 0 || slotIndex >= cumulativePositions.length - 1) return 0;
  
  // Position within the slot based on minutes
  const slotHeight = slotHeights[slotIndex];
  const minutesOffset = (minutes / 60) * slotHeight;
  const position = cumulativePositions[slotIndex] + minutesOffset;
  
  return Math.max(0, position);
}

// Calculate height using dynamic slot heights (for events spanning multiple slots)
function calculateHeightWithDynamicSlots(startTime, durationSeconds, slotHeights, cumulativePositions, startHour, baseSlotHeight) {
  // Minimum height for 2 lines of text: fontSize (10px) * lineHeight (1.3) * 2 lines = 26px
  // Plus padding: 3px top + 3px bottom = 6px
  // Plus potential recurring badge: ~10px
  // Total minimum: ~42px, but we'll use 44px to be safe
  const minHeightForTwoLines = 44;
  
  if (!startTime) return minHeightForTwoLines;
  
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const slotIndex = startHours - startHour;
  
  if (slotIndex < 0 || slotIndex >= slotHeights.length) {
    return minHeightForTwoLines;
  }
  
  // Use baseSlotHeight as reference for proportional calculations (consistent with slot height calculation)
  // If no duration, use minimum height
  if (!durationSeconds) return minHeightForTwoLines;
  
  // Calculate height based on duration using baseSlotHeight as reference
  const durationMinutes = durationSeconds / 60;
  const proportionalHeight = (durationMinutes / 60) * baseSlotHeight;
  
  // Ensure at least minimum height for 2 lines
  return Math.max(proportionalHeight, minHeightForTwoLines);
}

// Time-Grid Weekly View Component
function WeeklyView({ weekStart, selectedDate, schedulesByDate, onDateClick, onEventClick, getSchedulesForDate, isBooked }) {
  const weekDays = eachDayOfInterval({
    start: weekStart,
    end: endOfWeek(weekStart, { weekStartsOn: 0 })
  });

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const baseSlotHeight = 80; // Height of each hour slot in pixels when it has events
  const startHour = 0; // Start at 12 AM (midnight)
  const endHour = 23; // End at 11 PM (last slot goes to 11:59 PM)
  const timeSlots = generateTimeSlots(startHour, endHour);
  
  // Calculate dynamic slot heights based on events
  const { slotHeights, cumulativePositions, totalHeight } = 
    calculateDynamicSlotHeights(weekDays, getSchedulesForDate, timeSlots, startHour, baseSlotHeight);

  return (
    <div className="w-full border border-[var(--border-subtle)] rounded-lg overflow-hidden" style={{ background: 'var(--bg-surface)' }}>
      {/* Scrollable container with header inside */}
      <div className="relative" style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '800px' }}>
        {/* Day Headers - Sticky at top, inside scrollable container */}
        <div className="grid grid-cols-8 border-b border-[var(--border-subtle)] bg-primary-50 sticky top-0 z-30" style={{ gridTemplateColumns: '80px repeat(7, 1fr)', borderBottomWidth: '1px', boxSizing: 'border-box' }}>
          {/* Empty cell for time column */}
          <div className="p-1.5 border-r border-[var(--border-subtle)]" style={{ borderRightWidth: '1px', padding: '6px' }}></div>
          {/* Day headers */}
          {dayNames.map((dayName, index) => {
            const day = weekDays[index];
            const isToday = isSameDay(day, new Date());
            const isSelected = isSameDay(day, selectedDate);
            const daySchedules = getSchedulesForDate(day);

            return (
              <div
                key={index}
                onClick={() => onDateClick(day)}
                className={`text-center border-r border-[var(--border-subtle)] last:border-r-0 cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-primary-100'
                    : isToday
                    ? 'bg-primary-25'
                    : 'hover:bg-primary-100'
                }`}
                style={{ 
                  padding: '6px',
                  borderRightWidth: index < 6 ? '1px' : '0px'
                }}
              >
                <div className="text-[10px] font-semibold text-[var(--text-secondary)]">{dayName}</div>
                <div
                  className={`text-sm font-bold ${
                    isSelected ? 'text-[var(--text-primary)]' : isToday ? 'text-[var(--text-secondary)]' : 'text-[var(--text-primary)]'
                  }`}
                >
                  {format(day, 'd')}
                </div>
              </div>
            );
          })}
        </div>

        {/* Time Grid */}
        <div className="grid grid-cols-8" style={{ gridTemplateColumns: '80px repeat(7, 1fr)', minHeight: `${totalHeight}px`, boxSizing: 'border-box' }}>
          {/* Time Column */}
          <div className="border-r border-[var(--border-subtle)] bg-primary-50 sticky left-0 z-10" style={{ borderRightWidth: '1px' }}>
            {timeSlots.map((slot, index) => {
              const height = slotHeights[index];
              return (
                <div
                  key={index}
                  className="border-b border-primary-100 text-right pr-2 text-[var(--text-secondary)]"
                  style={{ 
                    height: `${height}px`,
                    fontSize: '10px',
                    lineHeight: `${height}px`,
                    paddingTop: '2px',
                    paddingBottom: '2px',
                    borderBottomWidth: '1px'
                  }}
                >
                  <span className="font-semibold">{slot.displayTime}</span>
                </div>
              );
            })}
          </div>

          {/* Day Columns */}
          {weekDays.map((day, dayIndex) => {
            const isSelected = isSameDay(day, selectedDate);
            const daySchedules = getSchedulesForDate(day);
            
            // Sort schedules by time first to ensure consistent stacking
            const sortedDaySchedules = [...daySchedules].sort((a, b) => {
              const timeA = a.startTime || '00:00:00';
              const timeB = b.startTime || '00:00:00';
              return timeA.localeCompare(timeB);
            });

            // Calculate positions using dynamic slot heights - proportional to time like Google Calendar
            const positionedSchedules = sortedDaySchedules.map(schedule => {
              // Calculate position proportionally within the slot (like Google Calendar)
              const [hours, minutes] = schedule.startTime.split(':').map(Number);
              const slotIndex = hours - startHour;
              
              if (slotIndex < 0 || slotIndex >= cumulativePositions.length - 1) {
                return null;
              }
              
              const slotTop = cumulativePositions[slotIndex];
              const slotHeight = slotHeights[slotIndex];
              
              // IMPORTANT: Use baseSlotHeight for position calculation to maintain consistent time positioning
              // The slotHeight may be expanded for stacking, but we want time positions to be consistent
              // Position within slot is proportional to minutes (0-60 minutes maps to 0-baseSlotHeight)
              // This ensures 10:00 is at top, 10:30 is at middle, 10:59 is near bottom
              const minutesInHour = minutes;
              const positionInSlot = (minutesInHour / 60) * baseSlotHeight;
              const top = slotTop + positionInSlot;
              
              // Calculate height based on duration, using baseSlotHeight as reference for consistency
              const minHeightForTwoLines = 44;
              let height = minHeightForTwoLines;
              if (schedule.video?.duration) {
                const durationMinutes = schedule.video.duration / 60;
                // Calculate proportional height using baseSlotHeight (consistent reference)
                const proportionalHeight = (durationMinutes / 60) * baseSlotHeight;
                // Use proportional height if it's at least the minimum
                height = Math.max(proportionalHeight, minHeightForTwoLines);
              }
              
              const endTime = schedule.startTime ? (() => {
                const durationMinutes = schedule.video?.duration ? schedule.video.duration / 60 : 60;
                const endMinutes = hours * 60 + minutes + durationMinutes;
                return endMinutes;
              })() : 0;
              
              const slotBottom = cumulativePositions[slotIndex + 1];
              
              return {
                ...schedule,
                top,
                height,
                endTime,
                left: 0,
                width: 1,
                slotIndex,
                slotTop,
                slotBottom
              };
            }).filter(s => s !== null && s.top < totalHeight && s.top + s.height > 0);
            
            // Cards positioned at proportional time, but if they overlap, place them below each other
            const fixedCardHeight = 35; // Fixed height for cards (shorter)
            const cardGap = 4; // Gap between stacked cards to show the top line of the card below

            const adjustedSchedules = [];
            for (let i = 0; i < positionedSchedules.length; i++) {
              const schedule = positionedSchedules[i];
              let cardTop = schedule.top;

              // Check if this card overlaps VISUALLY with any previous card
              // We need to check visual overlap, not just time overlap, because cards have fixed height
              let maxBottom = cardTop;
              for (let j = 0; j < i; j++) {
                const prevSchedule = adjustedSchedules[j];

                // Only check cards in the same time slot (same hour)
                if (prevSchedule.slotIndex === schedule.slotIndex) {
                  const prevTop = prevSchedule.top;
                  const prevBottom = prevSchedule.top + prevSchedule.height;

                  // Check if this card would visually overlap with the previous card
                  // Visual overlap = new card's top position is within previous card's space
                  if (cardTop < prevBottom) {
                    // They would overlap visually - position this card below the previous one with a gap
                    maxBottom = Math.max(maxBottom, prevBottom + cardGap);
                  }
                }
              }
              
              adjustedSchedules.push({
                ...schedule,
                top: maxBottom,
                height: fixedCardHeight,
                left: 0,
                width: 1
              });
            }

            return (
              <div
                key={dayIndex}
                className="relative border-r border-[var(--border-subtle)] last:border-r-0"
                style={{ 
                  minHeight: `${totalHeight}px`,
                  borderRightWidth: dayIndex < 6 ? '1px' : '0px'
                }}
                onClick={() => onDateClick(day)}
              >
                {/* Time slot grid lines */}
                {timeSlots.map((slot, slotIndex) => (
                  <div
                    key={slotIndex}
                    className="border-b border-primary-100"
                    style={{ 
                      height: `${slotHeights[slotIndex]}px`,
                      borderBottomWidth: '1px',
                      boxSizing: 'border-box'
                    }}
                  />
                ))}

                {/* Schedule Items */}
                {adjustedSchedules.map((schedule, scheduleIndex) => {
                  const timeDisplay = formatTime12Hour(schedule.startTime);
                  // Use the fixed card height - no adjustments needed
                  const displayHeight = schedule.height;

                  // Only filter if card is completely out of bounds
                  if (schedule.top > totalHeight + 100) return null;

                  // Check if this schedule is booked
                  const isScheduleBooked = isBooked(schedule.id);

                  return (
                    <div
                      key={schedule.id}
                      className={`absolute rounded border transition-all duration-200 cursor-pointer shadow-sm overflow-hidden ${
                        isScheduleBooked
                          ? 'border-l-4 border-l-green-500 border-t-green-200 border-r-green-200 border-b-green-200 bg-green-50 hover:bg-green-100'
                          : 'border-primary-300 bg-primary-100 hover:bg-primary-200 hover:border-primary-400'
                      }`}
                      style={{
                        top: `${schedule.top}px`,
                        left: `${schedule.left * 100}%`,
                        width: `calc(${schedule.width * 100}% - 4px)`,
                        height: `${displayHeight}px`,
                        marginLeft: '2px',
                        marginRight: '2px',
                        zIndex: 1
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(schedule);
                      }}
                      title={`${schedule.video?.title || 'Scheduled'} at ${timeDisplay}`}
                    >
                      <div className="h-full flex flex-col" style={{ padding: '3px 2px' }}>
                        <div className="text-[var(--text-primary)] font-medium flex-1" style={{ fontSize: '10px', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          <span className="font-bold text-[var(--text-primary)]" style={{ fontSize: '9px' }}>{timeDisplay}</span>
                          {' - '}
                          {schedule.seriesId && <Repeat className="inline w-2.5 h-2.5 text-purple-600 mr-0.5" style={{ verticalAlign: 'text-bottom' }} />}
                          {schedule.video?.title || 'Scheduled'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function CustomerSchedulePage() {
  const { user, studio } = useAuth();
  const queryClient = useQueryClient();
  
  // Initialize with today's date - ensure it's a fresh date object
  const getToday = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    today.setHours(0, 0, 0, 0);
    return today;
  };
  
  const [selectedDate, setSelectedDate] = useState(() => getToday());
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly' or 'monthly', default to weekly
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
  });
  const [currentWeek, setCurrentWeek] = useState(() => {
    const now = new Date();
    return startOfWeek(now, { weekStartsOn: 0 }); // Start of current week
  });
  const [selectedScheduleForModal, setSelectedScheduleForModal] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  // Track success states for sidebar cards by schedule ID
  const [sidebarBookingSuccess, setSidebarBookingSuccess] = useState({});
  const [sidebarCancelSuccess, setSidebarCancelSuccess] = useState({});

  // Get studio ID - prefer studio object, fallback to user.studioId
  const studioId = studio?.id || user?.studioId;
  
  // Force selectedDate to today on mount and when studioId is available
  useEffect(() => {
    const today = getToday();
    // Only update if selectedDate is not today
    if (!isSameDay(selectedDate, today)) {
      console.log('CustomerSchedule - Resetting selectedDate to today:', today);
      setSelectedDate(today);
    }
    // Ensure currentWeek is current week if in weekly view
    if (viewMode === 'weekly') {
      const now = new Date();
      const currentWeekStart = startOfWeek(now, { weekStartsOn: 0 });
      if (format(currentWeek, 'yyyy-MM-dd') !== format(currentWeekStart, 'yyyy-MM-dd')) {
        console.log('CustomerSchedule - Resetting currentWeek to current week:', currentWeekStart);
        setCurrentWeek(currentWeekStart);
      }
    } else {
      // Ensure currentMonth is current month
      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      if (format(currentMonth, 'yyyy-MM') !== format(currentMonthStart, 'yyyy-MM')) {
        console.log('CustomerSchedule - Resetting currentMonth to current month:', currentMonthStart);
        setCurrentMonth(currentMonthStart);
      }
    }
  }, [studioId, viewMode]); // Run when studioId or viewMode is available

  // Calculate date range based on view mode
  const calendarStart = viewMode === 'weekly' 
    ? startOfWeek(currentWeek, { weekStartsOn: 0 })
    : startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
  const calendarEnd = viewMode === 'weekly'
    ? endOfWeek(currentWeek, { weekStartsOn: 0 })
    : endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });

  // Fetch schedules for the visible period - match business portal exactly
  // Use same query key structure so they share cache
  const { data: schedules, isLoading, error } = useQuery({
    queryKey: ['schedules', studioId, viewMode === 'weekly' ? format(currentWeek, 'yyyy-MM-dd') : format(currentMonth, 'yyyy-MM')],
    queryFn: () => {
      // Fetch schedules for the month range - same as business portal
      const startDate = format(calendarStart, 'yyyy-MM-dd');
      const endDate = format(calendarEnd, 'yyyy-MM-dd');
      console.log('CustomerSchedule - Fetching schedules with:', { studioId, startDate, endDate });
      return schedulesAPI.getSchedules(studioId, { startDate, endDate });
    },
    enabled: !!studioId,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: false,
  });

  // Log any errors
  if (error) {
    console.error('CustomerSchedule - API Error:', error);
  }

  // Fetch student's bookings
  const { data: myBookingsData } = useQuery({
    queryKey: ['my-bookings', user?.id],
    queryFn: () => bookingsAPI.getMyBookings(user?.id),
    enabled: !!user?.id,
    retry: false,
    refetchInterval: false,
    refetchOnWindowFocus: true,
  });

  // The API interceptor returns response.data, so schedules is already the data object
  // Response structure: {success: true, data: {schedules: [...]}}
  // After interceptor: {success: true, data: {schedules: [...]}}
  const schedulesList = schedules?.data?.schedules || [];
  const myBookings = myBookingsData?.data || [];

  // Get thumbnails for all videos in schedules (cached globally)
  const videoIds = useMemo(() =>
    [...new Set(schedulesList.map(s => s.videoId).filter(Boolean))],
    [schedulesList]
  );
  const thumbnailsMap = useThumbnails(videoIds);
  
  
  // Create a map of schedule IDs to booking status
  const bookingMap = {};
  myBookings.forEach(booking => {
    if (booking.status === 'confirmed') {
      bookingMap[booking.scheduleId] = booking.id;
    }
  });

  // Create booking mutation
  const createBookingMutation = useMutation({
    mutationFn: (data) => bookingsAPI.createBooking(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['my-bookings']);
      queryClient.invalidateQueries(['schedules']);

      // Find the schedule to get details for toast
      try {
        const schedule = schedules?.find(s => s.id === variables.scheduleId);
        if (schedule && schedule.video) {
          const timeDisplay = schedule.startTime ? (() => {
            try {
              const [hours, minutes] = schedule.startTime.split(':');
              const hour12 = parseInt(hours) % 12 || 12;
              const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
              return `${hour12}:${minutes} ${ampm}`;
            } catch (e) {
              return '';
            }
          })() : '';
          toast.success(`Successfully booked ${schedule.video.title || 'class'}${timeDisplay ? ` for ${timeDisplay}` : ''}!`);
        } else {
          toast.success('Class booked successfully!');
        }
      } catch (error) {
        // If toast formatting fails, still show generic success
        console.error('Error formatting booking toast:', error);
        toast.success('Class booked successfully!');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to book class');
    }
  });

  // Cancel booking mutation
  const cancelBookingMutation = useMutation({
    mutationFn: (data) => bookingsAPI.cancelBooking(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['my-bookings']);
      queryClient.invalidateQueries(['schedules']);
      toast.success('Booking cancelled');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to cancel booking');
    }
  });

  const handleBookClass = (scheduleId) => {
    createBookingMutation.mutate({
      scheduleId,
      studentId: user.id
    });
  };

  const handleCancelBooking = (bookingId) => {
    cancelBookingMutation.mutate({
      bookingId,
      studentId: user.id
    });
  };

  // Organize schedules by date
  const schedulesByDate = useMemo(() => {
    const result = {};

    console.log('CustomerSchedule - Organizing schedules, total:', schedulesList.length);

    // Get all days in the visible calendar range
    const visibleDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    schedulesList.forEach(schedule => {
      console.log('CustomerSchedule - Processing schedule:', {
        id: schedule.id,
        dayOfWeek: schedule.dayOfWeek,
        specificDate: schedule.specificDate,
        recurrenceType: schedule.recurrenceType,
        startTime: schedule.startTime,
        isActive: schedule.isActive,
        className: schedule.className
      });

      // Handle schedules with specificDate or instanceDate
      if (schedule.specificDate || schedule.instanceDate) {
        const dateKey = format(parseISO(schedule.specificDate || schedule.instanceDate), 'yyyy-MM-dd');
        if (!result[dateKey]) {
          result[dateKey] = [];
        }
        result[dateKey].push(schedule);
      }
      // Also handle old-style recurring schedules with dayOfWeek
      else if (schedule.dayOfWeek !== null && schedule.dayOfWeek !== undefined) {
        // Recurring weekly schedule - show on all matching days of week
        visibleDays.forEach(day => {
          if (day.getDay() === schedule.dayOfWeek) {
            const dateKey = format(day, 'yyyy-MM-dd');
            if (!result[dateKey]) {
              result[dateKey] = [];
            }
            result[dateKey].push(schedule);
          }
        });
      }
    });

    console.log('CustomerSchedule - Organized schedules by date:', result);
    console.log('CustomerSchedule - Date keys:', Object.keys(result));

    // Sort schedules by time within each date
    Object.keys(result).forEach(dateKey => {
      result[dateKey].sort((a, b) => {
        const timeA = a.startTime || '00:00:00';
        const timeB = b.startTime || '00:00:00';
        return timeA.localeCompare(timeB);
      });
    });

    return result;
  }, [schedulesList, calendarStart, calendarEnd]);

  // Get schedules for a specific date
  const getSchedulesForDate = (date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return schedulesByDate[dateKey] || [];
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  const handleMonthChange = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = direction === 'next' ? addMonths(prev, 1) : subMonths(prev, 1);
      // Invalidate queries when month changes to refetch schedules
      queryClient.invalidateQueries(['schedules', studioId]);
      return newMonth;
    });
  };

  const handleWeekChange = (direction) => {
    setCurrentWeek(prev => {
      const newWeek = direction === 'next' ? addWeeks(prev, 1) : subWeeks(prev, 1);
      // Invalidate queries when week changes to refetch schedules
      queryClient.invalidateQueries(['schedules', studioId]);
      return newWeek;
    });
  };


  const isFull = (schedule) => {
    const current = schedule.current_bookings || 0;
    const max = schedule.max_capacity || 10;
    return current >= max;
  };

  const isBooked = (scheduleId) => {
    return bookingMap[scheduleId] !== undefined;
  };

  // Custom tile content for calendar
  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const daySchedules = getSchedulesForDate(date);
      if (daySchedules.length > 0) {
        return (
          <div className="mt-1 flex flex-wrap gap-0.5 justify-center">
            {daySchedules.slice(0, 3).map((schedule, idx) => (
              <div
                key={idx}
                className="w-1.5 h-1.5 rounded-full bg-primary-600"
                title={`${schedule.video?.title || 'Scheduled'} at ${schedule.startTime?.slice(0, 5) || ''}`}
              />
            ))}
            {daySchedules.length > 3 && (
              <div className="text-[8px] text-[var(--text-secondary)] font-semibold">
                +{daySchedules.length - 3}
              </div>
            )}
          </div>
        );
      }
    }
    return null;
  };

  // Custom tile className for highlighting dates with schedules
  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const classes = [];
      
      const viewingMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const viewingMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      const isCurrentMonth = date >= viewingMonthStart && date <= viewingMonthEnd;
      const isToday = isSameDay(date, new Date());
      
      if (!isCurrentMonth) {
        classes.push('other-month');
        return classes.join(' ');
      }
      
      if (isToday) {
        classes.push('today-in-month');
      }
      
      const daySchedules = getSchedulesForDate(date);
      if (daySchedules.length > 0) {
        classes.push('has-schedules');
      }
      if (isSameDay(date, selectedDate)) {
        classes.push('selected-date');
      }
      return classes.length > 0 ? classes.join(' ') : null;
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[var(--text-primary)]">Class Schedule</h1>
          <p className="text-[var(--text-secondary)] mt-2">Book your classes</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              setSelectedDate(today);
              if (viewMode === 'weekly') {
                setCurrentWeek(startOfWeek(today, { weekStartsOn: 0 }));
              } else {
                setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
              }
              queryClient.invalidateQueries(['schedules', studioId]);
            }}
            className="btn btn-secondary flex items-center gap-2"
          >
            <CalendarIcon className="w-4 h-4" />
            Today
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Calendar View */}
        <div className="lg:col-span-4">
          <div className="card p-4 hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[var(--border-subtle)]" style={{ maxHeight: '880px', display: 'flex', flexDirection: 'column' }}>
            <div className="flex items-center justify-between mb-4" style={{ flexShrink: 0 }}>
              <button
                onClick={() => viewMode === 'weekly' ? handleWeekChange('prev') : handleMonthChange('prev')}
                className="p-2 hover:bg-primary-50 rounded-lg transition-all duration-300 hover:shadow-md"
              >
                <ChevronLeft className="w-5 h-5 text-[var(--text-secondary)]" />
              </button>
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-bold text-[var(--text-primary)]">
                  {viewMode === 'weekly' 
                    ? `${format(calendarStart, 'MMM d')} - ${format(calendarEnd, 'MMM d, yyyy')}`
                    : format(currentMonth, 'MMMM yyyy')
                  }
                </h2>
                {/* View Mode Toggle - Near Calendar */}
                <div className="flex items-center gap-1 bg-primary-50 rounded-lg p-1">
                  <button
                    onClick={() => {
                      setViewMode('weekly');
                      const today = new Date();
                      setCurrentWeek(startOfWeek(today, { weekStartsOn: 0 }));
                      queryClient.invalidateQueries(['schedules', studioId]);
                    }}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                      viewMode === 'weekly'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'text-[var(--text-secondary)] hover:bg-primary-100'
                    }`}
                  >
                    Weekly
                  </button>
                  <button
                    onClick={() => {
                      setViewMode('monthly');
                      const today = new Date();
                      setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
                      queryClient.invalidateQueries(['schedules', studioId]);
                    }}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                      viewMode === 'monthly'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'text-[var(--text-secondary)] hover:bg-primary-100'
                    }`}
                  >
                    Monthly
                  </button>
                </div>
              </div>
              <button
                onClick={() => viewMode === 'weekly' ? handleWeekChange('next') : handleMonthChange('next')}
                className="p-2 hover:bg-primary-50 rounded-lg transition-all duration-300 hover:shadow-md"
              >
                <ChevronRight className="w-5 h-5 text-[var(--text-secondary)]" />
              </button>
            </div>

            <div style={{ flex: 1, minHeight: 0 }}>
              {viewMode === 'weekly' ? (
                <WeeklyView
                  weekStart={calendarStart}
                  selectedDate={selectedDate}
                  schedulesByDate={schedulesByDate}
                  onDateClick={handleDateClick}
                  onEventClick={setSelectedScheduleForModal}
                  getSchedulesForDate={getSchedulesForDate}
                  isBooked={isBooked}
                />
              ) : (
                <Calendar
                  value={selectedDate}
                  activeStartDate={currentMonth}
                  onActiveStartDateChange={({ activeStartDate }) => {
                    if (activeStartDate) {
                      setCurrentMonth(activeStartDate);
                    }
                  }}
                  tileContent={tileContent}
                  tileClassName={tileClassName}
                  onClickDay={handleDateClick}
                  className="w-full border-0"
                  navigationLabel={null}
                  formatMonthYear={(locale, date) => ''}
                  selectRange={false}
                  showNeighboringMonth={true}
                />
              )}
            </div>
          </div>

          {viewMode === 'weekly' && (
            <div className="mt-3 flex items-center gap-4 text-sm text-[var(--text-secondary)]">
              <div className="flex items-center gap-2">
                <div className="w-4 h-3 border-l-4 border-l-green-500 border-t border-r border-b border-green-200 bg-green-50 rounded"></div>
                <span>Attending (Booked)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-3 border border-primary-300 bg-primary-100 rounded"></div>
                <span>Not Attending (Available)</span>
              </div>
            </div>
          )}
        </div>

        {/* Selected Date Details - At A Glance */}
        <div className="lg:col-span-1" style={{ height: 'fit-content' }}>
          <div className="card hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[var(--border-subtle)] flex flex-col" style={{ maxWidth: '280px', padding: '12px', maxHeight: '880px' }}>
            <div className="mb-3 pb-2 border-b flex-shrink-0">
              <h3 className="font-bold text-[var(--text-primary)]" style={{ fontSize: '13px' }}>
                At A Glance
              </h3>
              <p className="text-[var(--text-secondary)]" style={{ fontSize: '11px' }}>
                {format(selectedDate, 'EEEE, MMM d')} • {getSchedulesForDate(selectedDate).length} {getSchedulesForDate(selectedDate).length === 1 ? 'class' : 'classes'}
              </p>
            </div>

            {/* Scrollable container - fills remaining space */}
            <div className="space-y-2 flex-1 overflow-y-auto overflow-x-hidden">
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : getSchedulesForDate(selectedDate).length > 0 ? (
                getSchedulesForDate(selectedDate).map((schedule) => {
                  const booked = isBooked(schedule.id);
                  const full = isFull(schedule);
                  const available = (schedule.max_capacity || 10) - (schedule.current_bookings || 0);

                  const video = schedule.video || {};
                  const categoryColors = {
                    yoga: 'text-purple-700',
                    dance: 'text-pink-700',
                    martial_arts: 'text-orange-700',
                    fitness: 'text-red-700',
                    pilates: 'text-blue-700',
                    meditation: 'text-indigo-700',
                    other: 'text-gray-700'
                  };
                  const categoryLabels = {
                    yoga: 'Yoga',
                    dance: 'Dance',
                    martial_arts: 'Martial Arts',
                    fitness: 'Fitness',
                    pilates: 'Pilates',
                    meditation: 'Meditation',
                    other: 'Other'
                  };

                  return (
                    <div
                      key={schedule.id}
                      className={`rounded-lg border group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden ${
                        booked ? 'border-l-4 border-l-green-500 border-t-green-200 border-r-green-200 border-b-green-200' : 'border-[var(--border-subtle)]'
                      }`}
                      style={{ background: 'var(--bg-elevated)' }}
                    >
                      {/* Compact thumbnail banner */}
                      <div
                        className="relative h-16 bg-gradient-to-br from-primary-100 to-secondary-100 cursor-pointer"
                        onClick={() => setSelectedScheduleForModal(schedule)}
                      >
                        {thumbnailsMap[schedule.videoId] ? (
                          <img
                            src={thumbnailsMap[schedule.videoId]}
                            alt={video.title || 'Class thumbnail'}
                            className="w-full h-full object-cover opacity-90"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Video className="w-6 h-6 text-[var(--text-muted)]" />
                          </div>
                        )}
                        {/* Category badge */}
                        {video.category && (
                          <div className="absolute top-1 right-1 text-xs font-semibold px-1.5 py-0.5 rounded-full shadow-lg flex items-center gap-0.5" style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}>
                            <span>{CATEGORIES.find(c => c.value === video.category)?.icon}</span>
                            <span className="hidden sm:inline">{CATEGORIES.find(c => c.value === video.category)?.label}</span>
                          </div>
                        )}
                        {/* Time badge overlay */}
                        <div className="absolute top-1 left-1 bg-primary-900 bg-opacity-90 text-white px-1.5 py-0.5 rounded text-xs font-semibold">
                          {formatTime12Hour(schedule.startTime)}
                        </div>
                        {booked && (
                          <div className="absolute bottom-1 right-1 bg-green-600 bg-opacity-90 text-white px-1.5 py-0.5 rounded text-xs flex items-center gap-0.5">
                            <CheckCircle className="w-3 h-3" />
                            Booked
                          </div>
                        )}
                      </div>

                      {/* Compact info section */}
                      <div className="p-2">
                        <h4
                          className="font-semibold text-[var(--text-primary)] text-sm line-clamp-1 mb-0.5 cursor-pointer hover:text-[var(--text-secondary)]"
                          onClick={() => setSelectedScheduleForModal(schedule)}
                        >
                          {video.title || 'Unknown Video'}
                        </h4>
                        <p className="text-[var(--text-secondary)] text-xs mb-1">
                          {video.instructor?.firstName} {video.instructor?.lastName}
                        </p>
                        <div className="flex items-center gap-1.5 flex-wrap mb-2">
                          {schedule.seriesId && (
                            <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs flex items-center gap-0.5">
                              <Repeat className="w-2.5 h-2.5" />
                              Recurring
                            </span>
                          )}
                          <span className="bg-primary-100 text-[var(--text-secondary)] px-1.5 py-0.5 rounded text-xs">
                            {(() => {
                              const durationSeconds = video.duration || 0;
                              const minutes = Math.floor(durationSeconds / 60);
                              const seconds = durationSeconds % 60;
                              if (minutes > 0 && seconds > 0) {
                                return `${minutes}m ${seconds}s`;
                              } else if (minutes > 0) {
                                return `${minutes}m`;
                              } else if (seconds > 0) {
                                return `${seconds}s`;
                              }
                              return '0m';
                            })()}
                          </span>
                          <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs flex items-center gap-0.5">
                            <Users className="w-2.5 h-2.5" />
                            {available} {available === 1 ? 'spot' : 'spots'} left
                          </span>
                          {available <= 3 && available > 0 && (
                            <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-xs">
                              Almost Full
                            </span>
                          )}
                        </div>

                        {/* Booking button */}
                        {sidebarBookingSuccess[schedule.id] ? (
                          <button
                            disabled
                            className="w-full btn btn-sm bg-green-600 text-white border-green-600 py-1.5 text-xs font-semibold flex items-center justify-center gap-1"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Booked! ✓
                          </button>
                        ) : sidebarCancelSuccess[schedule.id] ? (
                          <button
                            disabled
                            className="w-full btn btn-sm bg-blue-600 text-white border-blue-600 py-1.5 text-xs font-semibold flex items-center justify-center gap-1"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Cancelled! ✓
                          </button>
                        ) : booked ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSidebarCancelSuccess(prev => ({ ...prev, [schedule.id]: false }));
                              handleCancelBooking(bookingMap[schedule.id]);
                              setSidebarCancelSuccess(prev => ({ ...prev, [schedule.id]: true }));
                              setTimeout(() => {
                                setSidebarCancelSuccess(prev => ({ ...prev, [schedule.id]: false }));
                              }, 1500);
                            }}
                            disabled={cancelBookingMutation.isPending || sidebarCancelSuccess[schedule.id]}
                            className="w-full btn btn-sm bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700 py-1.5 text-xs font-semibold flex items-center justify-center gap-1"
                          >
                            {cancelBookingMutation.isPending ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                                Canceling...
                              </>
                            ) : (
                              <>
                                <XCircle className="w-3 h-3" />
                                Cancel
                              </>
                            )}
                          </button>
                        ) : full ? (
                          <button
                            disabled
                            className="w-full btn btn-sm bg-gray-400 text-white border-gray-400 py-1.5 text-xs font-semibold opacity-60 cursor-not-allowed"
                          >
                            Class Full
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSidebarBookingSuccess(prev => ({ ...prev, [schedule.id]: false }));
                              handleBookClass(schedule.id);
                              setSidebarBookingSuccess(prev => ({ ...prev, [schedule.id]: true }));
                              setTimeout(() => {
                                setSidebarBookingSuccess(prev => ({ ...prev, [schedule.id]: false }));
                              }, 1500);
                            }}
                            disabled={createBookingMutation.isPending || sidebarBookingSuccess[schedule.id]}
                            className="w-full btn btn-sm btn-primary py-1.5 text-xs font-semibold flex items-center justify-center gap-1"
                          >
                            {createBookingMutation.isPending ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                                Booking...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-3 h-3" />
                                Book
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <Video className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-2" />
                  <p className="text-sm text-[var(--text-muted)]">No classes scheduled</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Class Detail Modal */}
      {selectedScheduleForModal && (() => {
        // Get fresh schedule data from the schedules query to show real-time updates
        const freshSchedule = schedulesList?.find(s => s.id === selectedScheduleForModal.id) || selectedScheduleForModal;

        return (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedScheduleForModal(null)}
          >
            <div
              className="rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              style={{ background: 'var(--bg-surface)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Content */}
              <div className="relative">
              {/* Close Button */}
              <button
                onClick={() => setSelectedScheduleForModal(null)}
                className="absolute top-4 right-4 z-10 rounded-full p-2 shadow-lg transition-all duration-200 hover:opacity-100" style={{ background: 'rgba(255,255,255,0.9)' }}
              >
                <X className="w-5 h-5 text-[var(--text-primary)]" />
              </button>

              {/* Thumbnail Banner */}
              <div className="relative h-48 bg-gradient-to-br from-primary-100 to-secondary-100">
                {thumbnailsMap[freshSchedule.videoId] ? (
                  <img
                    src={thumbnailsMap[freshSchedule.videoId]}
                    alt={freshSchedule.video?.title || 'Class thumbnail'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Video className="w-24 h-24 text-[var(--text-muted)]" />
                  </div>
                )}

                {/* Duration Badge */}
                {freshSchedule.video?.duration && (
                  <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm px-3 py-2 rounded-lg flex items-center gap-2 shadow-md">
                    <Play className="w-4 h-4 text-white" />
                    <span className="font-semibold text-white text-sm">
                      {(() => {
                        const durationSeconds = freshSchedule.video.duration || 0;
                        const mins = Math.floor(durationSeconds / 60);
                        const secs = durationSeconds % 60;
                        return `${mins}:${secs.toString().padStart(2, '0')}`;
                      })()}
                    </span>
                  </div>
                )}

                {/* Category Badge */}
                {freshSchedule.video?.category && (
                  <div className="absolute top-4 right-16 px-3 py-1 rounded-lg shadow-md" style={{ background: 'var(--bg-elevated)' }}>
                    <span className="text-sm font-semibold text-[var(--text-primary)]">
                      {(() => {
                        const labels = {
                          yoga: 'Yoga',
                          dance: 'Dance',
                          martial_arts: 'Martial Arts',
                          fitness: 'Fitness',
                          pilates: 'Pilates',
                          meditation: 'Meditation',
                          other: 'Other'
                        };
                        return labels[freshSchedule.video.category] || 'Class';
                      })()}
                    </span>
                  </div>
                )}

                {/* Recurring Badge */}
                {freshSchedule.seriesId && (
                  <div className="absolute bottom-4 left-4 bg-purple-600 px-3 py-2 rounded-lg shadow-md flex items-center gap-2">
                    <Repeat className="w-4 h-4 text-white" />
                    <span className="text-sm font-semibold text-white">Recurring Class</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Title and Time */}
                <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
                  {freshSchedule.video?.title || 'Unknown Class'}
                </h2>

                {freshSchedule.startTime && (
                  <div className="flex items-center gap-2 text-[var(--text-secondary)] mb-4">
                    <Clock className="w-5 h-5 text-[var(--text-secondary)]" />
                    <span className="text-lg font-semibold">
                      {(() => {
                        const [hours, minutes] = freshSchedule.startTime.split(':');
                        const hour12 = parseInt(hours) % 12 || 12;
                        const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
                        return `${hour12}:${minutes} ${ampm}`;
                      })()}
                    </span>
                  </div>
                )}

                {/* Instructor */}
                {freshSchedule.video?.instructor && (
                  <div className="flex items-center gap-3 mb-4 p-3 bg-primary-50 rounded-lg">
                    {freshSchedule.video.instructor.photoUrl ? (
                      <img
                        src={freshSchedule.video.instructor.photoUrl}
                        alt={`${freshSchedule.video.instructor.firstName} ${freshSchedule.video.instructor.lastName}`}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary-200 flex items-center justify-center">
                        <Users className="w-6 h-6 text-[var(--text-secondary)]" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-[var(--text-secondary)]">Instructor</p>
                      <p className="font-semibold text-[var(--text-primary)]">
                        {freshSchedule.video.instructor.firstName} {freshSchedule.video.instructor.lastName}
                      </p>
                    </div>
                  </div>
                )}

                {/* Description */}
                {freshSchedule.video?.description && (
                  <div className="mb-4">
                    <h3 className="font-semibold text-[var(--text-primary)] mb-2">About this class</h3>
                    <p className="text-[var(--text-secondary)] leading-relaxed">
                      {freshSchedule.video.description}
                    </p>
                  </div>
                )}

                {/* Class Details */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {freshSchedule.video?.duration && (
                    <div className="p-3 bg-primary-50 rounded-lg">
                      <p className="text-xs text-[var(--text-secondary)] mb-1">Duration</p>
                      <div className="flex items-center gap-2">
                        <Play className="w-4 h-4 text-[var(--text-secondary)]" />
                        <span className="font-semibold text-[var(--text-primary)]">
                          {(() => {
                            const durationSeconds = freshSchedule.video.duration || 0;
                            const minutes = Math.floor(durationSeconds / 60);
                            const seconds = durationSeconds % 60;
                            if (minutes > 0 && seconds > 0) {
                              return `${minutes} min ${seconds} sec`;
                            } else if (minutes > 0) {
                              return `${minutes} min`;
                            } else if (seconds > 0) {
                              return `${seconds} sec`;
                            }
                            return '0 min';
                          })()}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="p-3 bg-primary-50 rounded-lg">
                    <p className="text-xs text-[var(--text-secondary)] mb-1">Availability</p>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-[var(--text-secondary)]" />
                      <span className="font-semibold text-[var(--text-primary)]">
                        {(freshSchedule.max_capacity || 10) - (freshSchedule.current_bookings || 0)} of {freshSchedule.max_capacity || 10} spots left
                      </span>
                    </div>
                  </div>
                </div>

                {/* Almost Full Warning */}
                {(() => {
                  const available = (freshSchedule.max_capacity || 10) - (freshSchedule.current_bookings || 0);
                  return available <= 3 && available > 0 && (
                    <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-orange-700 font-semibold text-sm">
                        ⚠️ Almost Full! Only {available} {available === 1 ? 'spot' : 'spots'} remaining
                      </p>
                    </div>
                  );
                })()}

                {/* Booking Button */}
                {(() => {
                  const booked = isBooked(selectedScheduleForModal.id);
                  const full = isFull(selectedScheduleForModal);

                  return (
                    <div className="mt-6">
                      {bookingSuccess ? (
                        <button
                          disabled
                          className="w-full btn py-3 text-lg font-semibold shadow-lg bg-green-600 border-green-600 text-white flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-5 h-5" />
                          Booked! ✓
                        </button>
                      ) : cancelSuccess ? (
                        <button
                          disabled
                          className="w-full btn py-3 text-lg font-semibold shadow-lg bg-blue-600 border-blue-600 text-white flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-5 h-5" />
                          Cancelled! ✓
                        </button>
                      ) : booked ? (
                        <button
                          onClick={() => {
                            setCancelSuccess(false);
                            handleCancelBooking(bookingMap[selectedScheduleForModal.id]);
                            setCancelSuccess(true);
                            // Show success for 1.5 seconds, then clear
                            setTimeout(() => {
                              setCancelSuccess(false);
                            }, 1500);
                          }}
                          disabled={cancelBookingMutation.isPending || cancelSuccess}
                          className="w-full btn btn-outline text-red-600 hover:bg-red-50 border-red-300 py-3 text-lg font-semibold flex items-center justify-center gap-2"
                        >
                          {cancelBookingMutation.isPending ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-red-600 border-t-transparent"></div>
                              Canceling...
                            </>
                          ) : (
                            <>
                              <XCircle className="w-5 h-5" />
                              Cancel Booking
                            </>
                          )}
                        </button>
                      ) : full ? (
                        <button
                          disabled
                          className="w-full btn btn-outline opacity-50 cursor-not-allowed py-3 text-lg font-semibold"
                        >
                          Class Full
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setBookingSuccess(false);
                            handleBookClass(selectedScheduleForModal.id);
                            setBookingSuccess(true);
                            // Show success for 1.5 seconds, then clear
                            setTimeout(() => {
                              setBookingSuccess(false);
                            }, 1500);
                          }}
                          disabled={createBookingMutation.isPending || bookingSuccess}
                          className="w-full btn btn-primary py-3 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                        >
                          {createBookingMutation.isPending ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                              Booking...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-5 h-5" />
                              Book This Class
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

